import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { Target, User, CheckSquare, AlertTriangle, ChevronRight } from "lucide-react";
import { StatusBadge, HealthBar } from "@/components/Badges";
import { gravatarUrl, initials, relTime } from "@/lib/utils-fos";

// Entity mini-cards rendered under an assistant message. We do best-effort
// fuzzy matching of entity names inside the AI response text.
export function ChatEntityCards({ text }) {
  const nav = useNavigate();
  const [entities, setEntities] = useState({ projects: [], people: [], tasks: [], escalations: [] });
  const [refs, setRefs] = useState({ projects: [], people: [], tasks: [], escalations: [] });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [projects, people, tasks, escalations] = await Promise.all([
          endpoints.projects(), endpoints.people(), endpoints.tasks(), endpoints.escalations(),
        ]);
        if (alive) setEntities({ projects, people, tasks, escalations });
      } catch (e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!text) return;
    const lowered = text.toLowerCase();
    const matchByName = (list, key = "name", minLen = 4) =>
      list.filter((it) => {
        const n = (it[key] || "").trim();
        return n.length >= minLen && lowered.includes(n.toLowerCase());
      }).slice(0, 3);
    setRefs({
      projects: matchByName(entities.projects, "name"),
      people: matchByName(entities.people, "name", 3),
      tasks: matchByName(entities.tasks, "title", 6),
      escalations: matchByName(entities.escalations, "reason", 8),
    });
  }, [text, entities]);

  const total = refs.projects.length + refs.people.length + refs.tasks.length + refs.escalations.length;
  if (total === 0) return null;

  return (
    <div className="mt-2 space-y-1.5" data-testid="chat-entity-cards">
      {refs.projects.map((p) => (
        <ProjectMiniCard key={`p-${p.id}`} project={p} onOpen={() => nav(`/projects?focus=${p.id}`)} />
      ))}
      {refs.people.map((p) => (
        <PersonMiniCard key={`u-${p.id}`} person={p} onOpen={() => nav(`/people?focus=${p.id}`)} />
      ))}
      {refs.tasks.map((t) => {
        const proj = entities.projects.find((p) => p.id === t.project_id);
        return <TaskMiniCard key={`t-${t.id}`} task={t} projectName={proj?.name} onOpen={() => nav(`/projects?focus=${t.project_id}&highlight=task-${t.id}`)} />;
      })}
      {refs.escalations.map((e) => (
        <EscalationMiniCard key={`e-${e.id}`} escalation={e} onOpen={() => nav(`/activity?focus=${e.id}`)} />
      ))}
    </div>
  );
}

function CardShell({ icon, badge, onOpen, testid, children }) {
  return (
    <button
      onClick={onOpen}
      data-testid={testid}
      className="w-full flex items-center gap-3 p-2.5 rounded-sm border border-neutral-200 bg-white hover:border-neutral-400 hover:bg-neutral-50 text-left transition-colors group"
    >
      <span className="shrink-0 text-neutral-500">{icon}</span>
      <div className="flex-1 min-w-0">{children}</div>
      {badge}
      <ChevronRight size={13} className="text-neutral-300 group-hover:text-neutral-700 shrink-0" />
    </button>
  );
}

function ProjectMiniCard({ project, onOpen }) {
  return (
    <CardShell icon={<Target size={14} />} onOpen={onOpen} testid={`entity-card-project-${project.id}`}
      badge={<StatusBadge status={project.status} />}>
      <p className="text-sm font-medium text-neutral-900 truncate">{project.name}</p>
      <div className="flex items-center gap-2 mt-1">
        <div className="flex-1 max-w-[160px]"><HealthBar score={project.health_score} /></div>
        {project.next_action && <p className="text-[11px] text-neutral-500 truncate flex-1">{project.next_action}</p>}
      </div>
    </CardShell>
  );
}

function PersonMiniCard({ person, onOpen }) {
  const src = person.avatar_url || gravatarUrl(person.email, 32);
  const [failed, setFailed] = useState(false);
  const avatar = src && !failed
    ? <img src={src} alt={person.name} onError={() => setFailed(true)} className="h-8 w-8 rounded-full object-cover" />
    : <div className="h-8 w-8 rounded-full bg-neutral-200 text-neutral-800 flex items-center justify-center text-[11px] font-semibold">{initials(person.name)}</div>;
  return (
    <CardShell icon={avatar} onOpen={onOpen} testid={`entity-card-person-${person.id}`}>
      <p className="text-sm font-medium text-neutral-900 truncate">{person.name}</p>
      <p className="text-[11px] text-neutral-500 truncate">{[person.role, person.company].filter(Boolean).join(" · ") || "—"}</p>
    </CardShell>
  );
}

function TaskMiniCard({ task, projectName, onOpen }) {
  const dot = task.status === "done" ? "bg-neutral-300" : task.status === "blocked" ? "bg-[#FF453A]" : task.status === "in_progress" ? "bg-neutral-900" : "bg-neutral-300";
  return (
    <CardShell icon={<CheckSquare size={14} />} onOpen={onOpen} testid={`entity-card-task-${task.id}`}
      badge={<span className="text-[10px] text-neutral-600 border border-neutral-200 px-1.5 py-0.5 rounded-sm capitalize flex items-center gap-1"><span className={`h-1.5 w-1.5 rounded-full ${dot}`} />{(task.status || "todo").replace("_", " ")}</span>}>
      <p className="text-sm font-medium text-neutral-900 truncate">{task.title}</p>
      <p className="text-[11px] text-neutral-500 truncate">{projectName || "—"}{task.due_date ? ` · due ${task.due_date.slice(0, 10)}` : ""}</p>
    </CardShell>
  );
}

function EscalationMiniCard({ escalation, onOpen }) {
  return (
    <CardShell icon={<AlertTriangle size={14} className="text-[#FF453A]" />} onOpen={onOpen} testid={`entity-card-escalation-${escalation.id}`}
      badge={<span className="text-[10px] bg-red-50 text-[#FF453A] px-1.5 py-0.5 rounded-sm">Urgency {escalation.urgency}/5</span>}>
      <p className="text-sm font-medium text-neutral-900 truncate">{escalation.reason}</p>
      <p className="text-[11px] text-neutral-500 truncate">{relTime(escalation.created_at)} · {escalation.status}</p>
    </CardShell>
  );
}
